import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authKeys } from '@/features/auth/queryKeys';
import { AccountApi } from '../AccountApi';
import type { SubaccountCreateReq, SubaccountUpdateReq } from '@/lib/types/Subaccount';

export function useCreateSubAccount() {
  const { authData, selectedCId } = useAuth();
  const queryClient = useQueryClient();
  const userCId = authData?.cpuser.core.cId ?? null;

  return useMutation({
    mutationFn: (req: SubaccountCreateReq) => {
      if (!selectedCId) throw new Error('No account selected');
      return AccountApi.createSubAccount(selectedCId, req);
    },
    onSuccess: () => {
      if (userCId) {
        queryClient.invalidateQueries({ queryKey: authKeys.customerInfo(userCId) });
      }
    },
  });
}

export function useUpdateSubAccount() {
  const { authData, selectedCId } = useAuth();
  const queryClient = useQueryClient();
  const userCId = authData?.cpuser.core.cId ?? null;

  return useMutation({
    mutationFn: ({ subCId, req }: { subCId: number; req: SubaccountUpdateReq }) => {
      if (!selectedCId) throw new Error('No account selected');
      return AccountApi.updateSubAccount(selectedCId, subCId, req);
    },
    onSuccess: () => {
      if (userCId) {
        queryClient.invalidateQueries({ queryKey: authKeys.customerInfo(userCId) });
      }
    },
  });
}

export function useDeleteSubAccount() {
  const { authData, selectedCId } = useAuth();
  const queryClient = useQueryClient();
  const userCId = authData?.cpuser.core.cId ?? null;

  return useMutation({
    mutationFn: (subCId: number) => {
      if (!selectedCId) throw new Error('No account selected');
      return AccountApi.deleteSubAccount(selectedCId, subCId);
    },
    onSuccess: () => {
      if (userCId) {
        queryClient.invalidateQueries({ queryKey: authKeys.customerInfo(userCId) });
      }
    },
  });
}
